import { useState } from 'react';
import { Add, DeleteOutline, OpenInNew } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { appUrl } from '@/utils';

import { FlowLink } from '../models';
import { interpolate, missingVariables } from '../utils/template';

interface Props {
  links: FlowLink[];
  variables: Record<string, string>;
  onChange: (links: FlowLink[]) => void;
}

const LinksPanel = ({ links, variables, onChange }: Props) => {
  const [editing, setEditing] = useState(false);

  const update = (index: number, patch: Partial<FlowLink>) => {
    const next = [...links];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  if (!links.length && !editing) {
    return (
      <Button size='small' startIcon={<Add />} onClick={() => setEditing(true)}>
        Agregar link
      </Button>
    );
  }

  return (
    <Stack spacing={1}>
      {!editing &&
        links.map((link) => {
          const missing = missingVariables(link.path, variables);
          const path = interpolate(link.path, variables);

          return missing.length ? (
            <Typography
              key={link.label}
              variant='body2'
              color='text.secondary'
            >
              {link.label} — falta {missing.join(', ')}
            </Typography>
          ) : (
            <Link
              key={link.label}
              href={appUrl(path)}
              target='_blank'
              rel='noreferrer'
              underline='hover'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {link.label}
              <OpenInNew fontSize='inherit' />
            </Link>
          );
        })}

      {editing &&
        links.map((link, index) => (
          <Stack key={index} direction='row' spacing={1}>
            <TextField
              size='small'
              label='Texto'
              value={link.label}
              onChange={(event) => update(index, { label: event.target.value })}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size='small'
              label='Ruta del panel'
              value={link.path}
              onChange={(event) => update(index, { path: event.target.value })}
              fullWidth
            />
            <IconButton
              size='small'
              aria-label='Quitar link'
              onClick={() => onChange(links.filter((_, i) => i !== index))}
            >
              <DeleteOutline fontSize='small' />
            </IconButton>
          </Stack>
        ))}

      <Stack direction='row' spacing={1}>
        <Button size='small' onClick={() => setEditing((value) => !value)}>
          {editing ? 'Listo' : 'Editar links'}
        </Button>
        {editing && (
          <Button
            size='small'
            startIcon={<Add />}
            onClick={() => onChange([...links, { label: '', path: '' }])}
          >
            Agregar
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default LinksPanel;
